import { useCallback, useEffect, useState } from "react";

import { useDeployment, useWallet } from "../../context";
import { V2_FACTORY_ABI, V2_PAIR_ABI } from "../../lib/abis";

import { resolveSymbol } from "./types";

import type { V2Position } from "./types";
import type { Address } from "viem";

export function useV2Positions() {
  const { deployment } = useDeployment();
  const { address, publicClient } = useWallet();

  const [positions, setPositions] = useState<V2Position[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchPositions = useCallback(async () => {
    if (!address || !deployment?.v2 || !deployment.tokens) return;
    setLoading(true);
    try {
      const tokens = deployment.tokens as unknown as Record<string, Address>;
      const factory = deployment.v2.factory;

      // Enumerate all pairs from the factory
      const pairCount = await publicClient.readContract({
        address: factory,
        abi: V2_FACTORY_ABI,
        functionName: "allPairsLength",
      });

      const count = Number(pairCount);
      if (count === 0) {
        setPositions([]);
        return;
      }

      // Fetch all pair addresses in parallel
      const pairAddresses = await Promise.all(
        Array.from({ length: count }, (_, i) =>
          publicClient.readContract({
            address: factory,
            abi: V2_FACTORY_ABI,
            functionName: "allPairs",
            args: [BigInt(i)],
          }),
        ),
      );

      // Fetch details for each pair in parallel
      const result: V2Position[] = [];
      const details = await Promise.allSettled(
        pairAddresses.map(async (pairAddress) => {
          const [lpBalance, totalSupply, reserves, token0, token1] =
            await Promise.all([
              publicClient.readContract({
                address: pairAddress,
                abi: V2_PAIR_ABI,
                functionName: "balanceOf",
                args: [address],
              }),
              publicClient.readContract({
                address: pairAddress,
                abi: V2_PAIR_ABI,
                functionName: "totalSupply",
              }),
              publicClient.readContract({
                address: pairAddress,
                abi: V2_PAIR_ABI,
                functionName: "getReserves",
              }),
              publicClient.readContract({
                address: pairAddress,
                abi: V2_PAIR_ABI,
                functionName: "token0",
              }),
              publicClient.readContract({
                address: pairAddress,
                abi: V2_PAIR_ABI,
                functionName: "token1",
              }),
            ]);

          const reserve0 = reserves[0];
          const reserve1 = reserves[1];
          const userShare0 =
            totalSupply > 0n ? (lpBalance * reserve0) / totalSupply : 0n;
          const userShare1 =
            totalSupply > 0n ? (lpBalance * reserve1) / totalSupply : 0n;

          const sym0 = resolveSymbol(token0, tokens);
          const sym1 = resolveSymbol(token1, tokens);

          return {
            pair: `${sym0}/${sym1}`,
            pairAddress,
            token0,
            token1,
            reserve0,
            reserve1,
            lpBalance,
            totalSupply,
            userShare0,
            userShare1,
          };
        }),
      );

      for (const d of details) {
        if (d.status === "fulfilled") {
          result.push(d.value);
        }
      }

      setPositions(result);
    } catch {
      // Reading may fail
    } finally {
      setLoading(false);
    }
  }, [address, deployment?.v2, deployment?.tokens, publicClient]);

  useEffect(() => {
    void fetchPositions();
  }, [fetchPositions]);

  return { positions, loading, refetch: fetchPositions };
}
